import { useState, useCallback, useMemo } from 'react';
import { FindVehicle } from 'services/VehicleService';
import { ResponseCode } from 'services/ContentService';
import { Vehicle } from 'types/expiryRenewal';
import { regNoRegex } from 'utils/vehicleHelpers';

export interface FieldErrors {
  Registration: string | null;
  Make: string | null;
  Model: string | null;
  YearOfManufacture: string | null;
}

export type TouchedFields = Record<keyof FieldErrors, boolean>;

const YEAR_REGEX = /^\d{4}$/;
const CURRENT_YEAR = new Date().getFullYear();

export const validateYear = (value: string): string | null => {
  if (!value) return 'Required';
  if (!YEAR_REGEX.test(value)) return 'Year must be 4 digits';
  const num = Number(value);
  if (num < 1886 || num > CURRENT_YEAR) return `Year must be between 1886 and ${CURRENT_YEAR}`;
  return null;
};

export const validateFields = (
  fields: Vehicle,
  registrationSearchError: boolean,
  isDuplicate: boolean,
): FieldErrors => ({
  Registration: (registrationSearchError ? 'Search error' : null)
    || (!fields.Registration ? 'Required' : null)
    || (isDuplicate ? 'Duplicate' : null),
  Make: !fields.Make ? 'Required' : null,
  Model: !fields.Model ? 'Required' : null,
  YearOfManufacture: validateYear(fields.YearOfManufacture),
});

const useVehicleForm = (
  initialFields: Vehicle,
  computeIsDuplicate: (_normalizedReg: string) => boolean,
) => {
  const [fields, setFields] = useState<Vehicle>(initialFields);
  const [searchedFuelType, setSearchedFuelType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registrationSearchError, setRegistrationSearchError] = useState(false);
  const [touched, setTouched] = useState<TouchedFields>({
    Registration: false,
    Make: false,
    Model: false,
    YearOfManufacture: false,
  });

  const isDuplicate = useMemo(() => {
    const normalizedReg = fields.Registration.replace(/\s/g, '').toUpperCase();
    return !!normalizedReg && computeIsDuplicate(normalizedReg);
  }, [fields.Registration, computeIsDuplicate]);

  const errors = useMemo(
    () => validateFields(fields, registrationSearchError, isDuplicate),
    [fields, registrationSearchError, isDuplicate],
  );

  const searchAndRegError = isDuplicate
    ? 'This registration already exists in your vehicle list.'
    : null;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'YearOfManufacture') value = value.replace(/\D/g, '').slice(0, 4);
    setFields((prev) => ({ ...prev, [name]: value }));
    if (name === 'Registration') setRegistrationSearchError(false);
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleBlur = useCallback((field: keyof FieldErrors) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleSearch = useCallback(async () => {
    setTouched((prev) => ({ ...prev, Registration: true }));
    const reg = fields.Registration.replace(/\s/g, '').toUpperCase();
    if (!reg || !regNoRegex.test(reg)) {
      setRegistrationSearchError(true);
      return;
    }
    setLoading(true);
    setRegistrationSearchError(false);
    try {
      const result = await FindVehicle({ Registration: reg });
      if (result.responseCode === ResponseCode.SUCCESS && result.payload) {
        setFields({ ...result.payload, Registration: reg });
        setSearchedFuelType(result.payload.FuelType ?? null);
        setTouched({ Registration: true, Make: true, Model: true, YearOfManufacture: true });
        setRegistrationSearchError(false);
      } else {
        setRegistrationSearchError(true);
      }
    } catch {
      setRegistrationSearchError(true);
    } finally {
      setLoading(false);
    }
  }, [fields.Registration]);

  const clearRegistrationSearchError = useCallback(() => setRegistrationSearchError(false), []);

  return {
    fields,
    searchedFuelType,
    touched,
    loading,
    errors,
    searchAndRegError,
    isVehicleNotFound: registrationSearchError,
    clearRegistrationSearchError,
    setTouched,
    handleChange,
    handleBlur,
    handleSearch,
  };
};

export default useVehicleForm;
